// SPDX-License-Identifier: AGPL-3.0-or-later

import {getCachedNumberFormat} from '@app/features/i18n/utils/IntlCache';
import type {RecurringQuote} from '@fluxer/schema/src/domains/premium/PaddleSubscriptionSchemas';
import type {
	ShopChargeCurrency,
	ShopPayable,
	ShopViewerPayment,
} from '@fluxer/schema/src/domains/shop/ShopPaymentSchemas';
import {formatShopAzn, formatShopTry, formatShopUsd} from '@fluxer/schema/src/domains/shop/ShopPriceFormatting';
import type {ShopPriceResponse, ShopQuoteResponse} from '@fluxer/schema/src/domains/shop/ShopResponseSchemas';

export function formatPayable(minor: number, currency: ShopChargeCurrency, locale: string): string {
	return getCachedNumberFormat(locale, {style: 'currency', currency, currencyDisplay: 'narrowSymbol'}).format(
		minor / 100,
	);
}

export function formatRecurringAmount(quote: RecurringQuote, locale: string): string {
	if (quote.payable) return formatPayable(quote.payable.amount_minor, quote.payable.currency, locale);
	return (quote.currency === 'USD' ? formatUsd : formatTry)(quote.amount_minor, locale);
}

// The price levels in the minor unit of the payable currency, so the display steps below work unchanged. A viewer who
// gets the member discount (where premium_usd_cents names one) pays it; its campaign price is the server's campaign
// step on the list price. Payriff in TL prices them in the admin's lira (try_minor), the manat being its value; without
// a TL rate the server charges no lira, so try_minor is dropped and the levels stay in manat.
export function inPayableUnits<T extends ShopPriceResponse>(
	price: T,
	memberViewer: boolean,
	tryRateMicro: number | null | undefined,
): T {
	const {payable, list_payable: list, plus_payable: plus} = price;
	if (!payable || !list) return price;
	const lira = tryRateMicro != null && payable.try_minor != null && list.try_minor != null;
	const units = (amount: ShopPayable) => (lira ? amount.try_minor! : amount.amount_minor);
	const manat = (amount: ShopPayable): ShopPayable => ({amount_minor: amount.amount_minor, currency: amount.currency});
	const member = memberViewer && price.premium_usd_cents !== null;
	return {
		...price,
		...(tryRateMicro == null && {
			payable: manat(payable),
			list_payable: manat(list),
			plus_payable: plus && manat(plus),
		}),
		base_usd_cents: units(list),
		sale_usd_cents: member ? percentOff(units(list), price.campaign_percent ?? 0) : units(payable),
		premium_usd_cents: member ? units(payable) : plus ? units(plus) : null,
	};
}

// The currency of a catalog price's levels once in payable units; null: US cents from an older server.
export function priceCurrency(price: ShopPriceResponse): ShopChargeCurrency | null {
	if (!price.payable || !price.list_payable) return null;
	return price.payable.try_minor != null && price.list_payable.try_minor != null ? 'TRY' : price.payable.currency;
}

// A server that names payable amounts names none for a price this viewer is not sold; an older server names none at all.
export function unsoldPrice(price: ShopPriceResponse): boolean {
	return price.payable !== undefined && priceCurrency(price) === null;
}

export function formatTry(minor: number, locale: string): string {
	return formatShopTry(minor, locale);
}

// The amount only ("8,50"); the message around it names the currency.
export function formatAzn(minor: number, locale: string): string {
	return formatShopAzn(minor, locale);
}

export function formatUsd(minor: number, locale: string): string {
	return formatShopUsd(minor, locale);
}

// How a quote or an order is charged. Payriff always charges AZN and is shown as the viewer's catalog shows it.
export function chargePayment(
	price: Pick<ShopQuoteResponse, 'provider' | 'currency'>,
	viewer: ShopViewerPayment | null | undefined,
): ShopViewerPayment {
	if (price.provider === 'paddle' && price.currency !== 'AZN') return {provider: 'paddle', currency: price.currency};
	return viewer?.provider === 'payriff' ? viewer : {provider: 'payriff', currency: 'TRY'};
}

// What the provider takes, in the minor unit of currency; an older server names only the AZN amount.
export function chargeMinor(price: Pick<ShopQuoteResponse, 'charge_amount_minor' | 'amount_azn_minor'>): number {
	return price.charge_amount_minor ?? price.amount_azn_minor;
}

// Older servers expose only the decimal rate; prefer the precise integer whenever it is supplied.
export function usdToAznRateMicro(
	price: {usd_to_azn_rate_micro?: number | null; usd_to_azn_rate?: string} | null | undefined,
): number | null {
	const rate = price?.usd_to_azn_rate_micro ?? Math.round(Number(price?.usd_to_azn_rate) * 1_000_000);
	return Number.isSafeInteger(rate) && rate > 0 ? rate : null;
}

// Keep the exact integer catalog rate: the legacy four-decimal string loses qepik precision.
// This is the same half-up formula as the server's computeShopPrice.
export function usdCentsToAznMinor(cents: number, rateMicro: number): number {
	return Math.floor((2 * cents * rateMicro + 1_000_000) / 2_000_000);
}

// The server already hides stale automatic rates. This TL is informational: the client never prices an order with it
// (a Payriff lira price is the server's, try_minor).
export function aznMinorToTryMinor(minor: number, rateMicro: number | null | undefined): number | null {
	return rateMicro == null ? null : Math.floor((2 * minor * 1_000_000 + rateMicro) / (2 * rateMicro));
}

// What Payriff charges for a lira price shown in TL: its manat value at the CBAR rate, the same half-up step as above.
export function tryMinorToAznMinor(minor: number, rateMicro: number | null | undefined): number | null {
	return rateMicro == null ? null : usdCentsToAznMinor(minor, rateMicro);
}

// Paddle refuses a charge under its minimum (min_charge_minor, in the charged currency), so such a price is not sold
// to this viewer. Paddle lira follows the server's chain: US cents to AZN to TL, each rounded half up.
export function belowChargeMinimum(
	cents: number,
	payment: ShopViewerPayment | null | undefined,
	settings: {
		usd_to_azn_rate_micro?: number | null;
		usd_to_azn_rate?: string;
		try_to_azn_rate_micro?: number | null;
	} | null,
): boolean {
	if (payment?.provider !== 'paddle' || payment.min_charge_minor == null) return false;
	let charge: number | null = cents;
	if (payment.currency === 'TRY') {
		const rate = usdToAznRateMicro(settings);
		charge =
			rate === null ? null : aznMinorToTryMinor(usdCentsToAznMinor(cents, rate), settings?.try_to_azn_rate_micro);
	}
	return charge !== null && charge < payment.min_charge_minor;
}

// TR "-%20", EN "-20%".
export function formatDiscount(percent: number, locale: string): string {
	return getCachedNumberFormat(locale, {style: 'percent'}).format(-percent / 100);
}

// One step of the server's computeShopPrice: `percent` off, rounded half up.
export function percentOff(cents: number, percent: number): number {
	return Math.floor((2 * cents * (100 - percent) + 100) / 200);
}

// Card prices are display only. A campaign shows until its end on the server clock; the server enforces the end.
export function campaignShown(price: ShopPriceResponse, now: number): boolean {
	return (
		price.campaign_percent !== null && (price.campaign_ends_at === null || Date.parse(price.campaign_ends_at) > now)
	);
}

// The price a card shows now: the campaign price while the campaign shows, the list price otherwise.
export function shownCents(price: ShopPriceResponse, now: number): number {
	return campaignShown(price, now) ? price.sale_usd_cents : price.base_usd_cents;
}
